/**
 * Utilitaires Ledgera partagés (erreurs API, résolution compte / moyen de paiement, détection d’écritures liées).
 */

/** Détail lisible depuis une erreur Axios / FastAPI (`detail` string ou liste de validation). */
export function formatLedgerApiError(err, fallback = 'Erreur Ledgera.') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (Array.isArray(d) && d.length) {
    return d
      .map((x) => {
        if (x && typeof x === 'object') {
          const loc = Array.isArray(x.loc) ? x.loc.join('.') : '';
          const msg = x.msg || x.message || '';
          return [loc, msg].filter(Boolean).join(': ') || JSON.stringify(x);
        }
        return String(x);
      })
      .join(' ');
  }
  if (d && typeof d === 'object' && String(d.msg || d.message || '').trim()) {
    return String(d.msg || d.message).trim();
  }
  const m = String(err?.message || '').trim();
  return m || fallback;
}

/**
 * Aligne un identifiant de compte (ex. `ledger_account_id` d'un moyen de paiement) sur
 * l'`id` renvoyé par `list_accounts`, pour éviter « Account not found » (référence obsolète ou format différent).
 */
export function resolveCanonicalLedgerAccountId(rawAccountId, accounts) {
  const want = String(rawAccountId ?? '').trim();
  if (!want) return null;
  const list = Array.isArray(accounts) ? accounts : [];
  for (const a of list) {
    const id = String(a?.id ?? '').trim();
    if (id && id === want) return id;
  }
  const low = want.toLowerCase();
  for (const a of list) {
    const id = String(a?.id ?? '').trim();
    if (id && id.toLowerCase() === low) return id;
  }
  return null;
}

export function isEntreaContributionLedgerTx(tx) {
  return (
    tx?.metadata?.source_app === 'entrea' &&
    tx?.metadata?.source_type === 'contribution' &&
    Boolean(String(tx?.metadata?.source_id || '').trim())
  );
}

/**
 * Cartographie des IDs cotisation Entrea ayant déjà une écriture Ledgera (en attente ou postée).
 * @param {{ listTransactions: (params: object) => Promise<{ data?: { transactions?: unknown[] } }> }} ledgerApi
 * @returns {Promise<Record<string, true>>}
 */
export async function fetchLedgerLinkedContributionIds(ledgerApi) {
  const map = {};
  if (!ledgerApi?.listTransactions) return map;
  try {
    const [pendingRes, postedRes] = await Promise.all([
      ledgerApi.listTransactions({
        status: 'pending_validation',
        category: 'cotisation',
        limit: 500,
      }),
      ledgerApi.listTransactions({ status: 'posted', category: 'cotisation', limit: 500 }),
    ]);
    const txs = [
      ...(pendingRes?.data?.transactions || []),
      ...(postedRes?.data?.transactions || []),
    ];
    for (const tx of txs) {
      if (!isEntreaContributionLedgerTx(tx)) continue;
      const sid = String(tx?.metadata?.source_id || '').trim();
      if (sid) map[sid] = true;
    }
  } catch (e) {
    console.warn('fetchLedgerLinkedContributionIds:', e);
  }
  return map;
}

export async function resolveLedgerOutflowPaymentAccount(ledgerApi) {
  const [pmRes, accRes] = await Promise.all([
    ledgerApi.listPaymentMethods({ include_inactive: false, limit: 200 }),
    ledgerApi.listAccounts({ include_inactive: false }),
  ]);
  const withAcc = (pmRes?.data?.payment_methods || []).filter((pm) => pm?.ledger_account_id);
  const pmRow = withAcc.find((pm) => pm.is_default) || withAcc[0];
  const accounts = accRes?.data?.accounts || [];
  const linked = pmRow?.ledger_account_id ? String(pmRow.ledger_account_id).trim() : '';
  const canonical = linked ? resolveCanonicalLedgerAccountId(linked, accounts) : null;
  if (canonical) {
    return { payment_method_id: undefined, account_id: canonical };
  }
  const payment_method_id = pmRow?.id ? String(pmRow.id).trim() : undefined;
  const account_id = payment_method_id ? undefined : accounts[0]?.id ? String(accounts[0].id).trim() : undefined;
  return { payment_method_id, account_id };
}
